/**
 * Column model + CSV export for the per-day daily condition table.
 *
 * Columns are discovered from the user_profile snapshots themselves (not a fixed list), so
 * every key the model actually received shows up — including keys the backend adds later.
 * Known keys get a stable order; unknown ones follow in discovery order.
 */

import type { DailyConditionTableRow } from '../api/dailyConditions';

export type ProfileGroup = 'user' | 'device' | 'custom';

export const PROFILE_GROUPS: ProfileGroup[] = ['user', 'device', 'custom'];

export interface ProfileColumn {
  group: ProfileGroup;
  /** Path inside the group, e.g. ['load', 'tsb'] for device.load.tsb */
  path: string[];
  /** Dotted key used for headers and CSV, e.g. "device.load.tsb" */
  key: string;
}

export interface ResultColumn {
  key: string;
  get: (row: DailyConditionTableRow) => unknown;
}

// Objects nested deeper than this inside a group become one summarized cell
// (e.g. device.latest_session.zone_times_sec, device.hr.resting_hr).
const MAX_DEPTH = 2;

const KEY_ORDER: Record<string, string[]> = {
  user: ['name', 'language', 'gender', 'birth_date'],
  device: [
    'platform',
    'timezone',
    'device_source',
    'load',
    'latest_session',
    'weekly',
    'trend7d',
    'last4_weeks',
    'hr',
    'vdot',
  ],
  custom: ['states', 'training_load', 'last_workout', 'recent_runs', 'vitals', 'profile', 'medical_conditions'],
  'device.load': ['atl', 'ctl', 'tsb', 'acwr'],
  'custom.training_load': ['atl', 'ctl', 'tsb', 'acwr'],
  'custom.states': ['coaching', 'recovery', 'vitality', 'training_load'],
  'device.latest_session': ['date', 'distance_km', 'duration_min', 'avg_pace', 'avg_hr', 'max_hr', 'trimp'],
};

export const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const getPath = (value: unknown, path: string[]): unknown => {
  let current = value;
  for (const key of path) {
    if (!isPlainObject(current)) return undefined;
    current = current[key];
  }
  return current;
};

const rank = (parentKey: string, key: string): number => {
  const order = KEY_ORDER[parentKey];
  const index = order ? order.indexOf(key) : -1;
  return index === -1 ? Number.MAX_SAFE_INTEGER : index;
};

export const buildProfileColumns = (rows: DailyConditionTableRow[]): ProfileColumn[] => {
  const columns = new Map<string, ProfileColumn>();
  // First time each key prefix (e.g. "device.load") was seen — keeps siblings together.
  const firstSeen = new Map<string, number>();

  const visit = (group: ProfileGroup, path: string[], value: unknown) => {
    if (path.length > 0) {
      const prefix = [group, ...path].join('.');
      if (!firstSeen.has(prefix)) firstSeen.set(prefix, firstSeen.size);
    }
    if (isPlainObject(value) && path.length < MAX_DEPTH && Object.keys(value).length > 0) {
      for (const [key, child] of Object.entries(value)) visit(group, [...path, key], child);
      return;
    }
    if (path.length === 0) return;
    const key = [group, ...path].join('.');
    if (!columns.has(key)) columns.set(key, { group, path, key });
  };

  for (const row of rows) {
    for (const group of PROFILE_GROUPS) {
      const value = row.user_profile?.[group];
      if (isPlainObject(value)) visit(group, [], value);
    }
  }

  const compare = (a: ProfileColumn, b: ProfileColumn): number => {
    const groupDiff = PROFILE_GROUPS.indexOf(a.group) - PROFILE_GROUPS.indexOf(b.group);
    if (groupDiff) return groupDiff;
    for (let i = 0; i < Math.min(a.path.length, b.path.length); i++) {
      if (a.path[i] === b.path[i]) continue;
      const parentKey = [a.group, ...a.path.slice(0, i)].join('.');
      const rankDiff = rank(parentKey, a.path[i]) - rank(parentKey, b.path[i]);
      if (rankDiff) return rankDiff;
      const prefixA = [a.group, ...a.path.slice(0, i + 1)].join('.');
      const prefixB = [b.group, ...b.path.slice(0, i + 1)].join('.');
      return (firstSeen.get(prefixA) ?? 0) - (firstSeen.get(prefixB) ?? 0);
    }
    return a.path.length - b.path.length;
  };

  return [...columns.values()].sort(compare);
};

export const profileValue = (row: DailyConditionTableRow, column: ProfileColumn): unknown =>
  getPath(row.user_profile?.[column.group], column.path);

// Internal/test accounts (roopinNNN, testNNN, …) — each term is excluded on request, never by default
export const EXCLUDABLE_NAME_TERMS = ['roopin', 'test'] as const;
export type ExcludableTerm = (typeof EXCLUDABLE_NAME_TERMS)[number];

/** True when the nickname contains any of `terms` (case-insensitive). */
export const isExcludedAccount = (row: DailyConditionTableRow, terms: readonly string[]): boolean => {
  const name = (row.user_name ?? '').toLowerCase();
  return terms.some((term) => name.includes(term));
};

export const RESULT_COLUMNS: ResultColumn[] = [
  { key: 'status', get: (row) => row.status },
  { key: 'risk_flag', get: (row) => row.result?.risk_flag },
  { key: 'message', get: (row) => row.result?.message },
  { key: 'reason', get: (row) => row.result?.reason },
  { key: 'suspected_injury', get: (row) => row.result?.suspected_injury },
  { key: 'daily_guide.type', get: (row) => getPath(row.result, ['daily_guide', 'type']) },
  { key: 'daily_guide.description', get: (row) => getPath(row.result, ['daily_guide', 'description']) },
  { key: 'error_code', get: (row) => row.error_code },
];

const isPrimitive = (value: unknown): boolean => value === null || typeof value !== 'object';

const formatNumber = (value: number): string => String(Math.round(value * 10) / 10);

/** One-line text for a table cell. Long arrays/objects get a summary; the cell expands to full JSON. */
export const summarize = (value: unknown): { text: string; expandable: boolean } => {
  if (value === undefined) return { text: '', expandable: false };
  if (isPrimitive(value)) return { text: value === null ? 'null' : String(value), expandable: false };

  const json = JSON.stringify(value);
  if (Array.isArray(value)) {
    if (value.every(isPrimitive) && json.length <= 40) return { text: json, expandable: false };
    const parts = [`${value.length}개`];
    const sum = (field: string): number | null => {
      const nums = value.map((v) => (isPlainObject(v) ? v[field] : undefined)).filter((v) => typeof v === 'number');
      return nums.length ? (nums as number[]).reduce((a, b) => a + b, 0) : null;
    };
    const trimp = sum('trimp');
    const distance = sum('distance_km');
    if (trimp !== null) parts.push(`TRIMP 합 ${formatNumber(trimp)}`);
    if (distance !== null) parts.push(`${formatNumber(distance)} km`);
    return { text: parts.join(' · '), expandable: value.length > 0 };
  }

  const entries = Object.entries(value as Record<string, unknown>);
  if (entries.length === 0) return { text: '{}', expandable: false };
  const text = entries
    .map(([k, v]) => `${k}: ${isPrimitive(v) ? String(v) : Array.isArray(v) ? `[${v.length}]` : '{…}'}`)
    .join(', ');
  return { text, expandable: true };
};

const csvCell = (value: unknown): string => {
  if (value === undefined || value === null) return '';
  const text = typeof value === 'object' ? JSON.stringify(value) : String(value);
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

/**
 * Every row and every column — arrays/objects are written as JSON.
 * `dayOf` adds a leading `day` column (multi-day export).
 */
export const toCsv = (rows: DailyConditionTableRow[], dayOf?: (row: DailyConditionTableRow) => string): string => {
  const profileColumns = buildProfileColumns(rows);
  const header = [
    ...(dayOf ? ['day'] : []),
    'ai_user_id',
    'nickname',
    ...profileColumns.map((c) => c.key),
    ...RESULT_COLUMNS.map((c) => `result.${c.key}`),
    'created_at',
    'completed_at',
    'daily_condition_id',
  ];
  const lines = rows.map((row) =>
    [
      ...(dayOf ? [dayOf(row)] : []),
      row.user_id,
      row.user_name,
      ...profileColumns.map((c) => profileValue(row, c)),
      ...RESULT_COLUMNS.map((c) => c.get(row)),
      row.created_at,
      row.completed_at,
      row.id,
    ]
      .map(csvCell)
      .join(','),
  );
  return [header.map(csvCell).join(','), ...lines].join('\r\n');
};

/** UTC days from `first` to `last` inclusive (YYYY-MM-DD), oldest first. */
export const utcDaysBetween = (first: string, last: string): string[] => {
  const days: string[] = [];
  // Timestamps may carry any offset — normalize to the UTC calendar day first
  const utcMidnight = (value: string) => Date.parse(`${new Date(value).toISOString().slice(0, 10)}T00:00:00Z`);
  const end = utcMidnight(last);
  for (let t = utcMidnight(first); t <= end; t += 86_400_000) {
    days.push(new Date(t).toISOString().slice(0, 10));
  }
  return days;
};

export const downloadCsv = (filename: string, csv: string): void => {
  // BOM so Excel opens UTF-8 (Korean/Estonian text) correctly
  const blob = new Blob(['\ufeff', csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};
